import { useCallback, useEffect, useMemo, useState, } from 'react';
import TagManager from 'react-gtm-module';
import { COOKIE_CONSENT_KEY, COOKIE_CONSENT_TTL_MS, CURRENT_CONSENT_VERSION, GTM_CONTAINER_ID, isConsentValid, ls, } from '@/stores/local-storage';
const CONSENT_CHANGED_EVENT = 'cc-cookie-consent-changed';
export function useCookieConsent() {
    const [consentGiven, setConsentGiven] = useState(() => {
        const stored = ls.getItem(COOKIE_CONSENT_KEY);
        return stored != null && isConsentValid(stored) ? stored.value : null;
    });
    const initialCategories = useMemo(() => {
        var _a;
        if (consentGiven == null)
            return null;
        const stored = ls.getItem(COOKIE_CONSENT_KEY);
        return stored != null && isConsentValid(stored) ? ((_a = stored.categories) !== null && _a !== void 0 ? _a : null) : null;
    }, [consentGiven]);
    useEffect(() => {
        const handler = (e) => {
            const { detail } = e;
            setConsentGiven(detail.value);
        };
        window.addEventListener(CONSENT_CHANGED_EVENT, handler);
        return () => {
            window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
        };
    }, []);
    const accept = useCallback(() => {
        ls.setItem(COOKIE_CONSENT_KEY, {
            value: 'accepted',
            expiresAt: Date.now() + COOKIE_CONSENT_TTL_MS,
            version: CURRENT_CONSENT_VERSION,
            categories: { analytics: true },
        });
        setConsentGiven('accepted');
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { value: 'accepted' } }));
        TagManager.initialize({ gtmId: GTM_CONTAINER_ID });
    }, []);
    const decline = useCallback(() => {
        ls.setItem(COOKIE_CONSENT_KEY, {
            value: 'declined',
            expiresAt: Date.now() + COOKIE_CONSENT_TTL_MS,
            version: CURRENT_CONSENT_VERSION,
            categories: { analytics: false },
        });
        setConsentGiven('declined');
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { value: 'declined' } }));
    }, []);
    const save = useCallback((categories) => {
        const value = categories.analytics ? 'accepted' : 'declined';
        ls.setItem(COOKIE_CONSENT_KEY, {
            value,
            expiresAt: Date.now() + COOKIE_CONSENT_TTL_MS,
            version: CURRENT_CONSENT_VERSION,
            categories,
        });
        setConsentGiven(value);
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { value } }));
        // Only initialize trackers on first acceptance — if user already consented,
        // trackers are already running; re-initializing GTM would duplicate events.
        if (categories.analytics && consentGiven !== 'accepted') {
            TagManager.initialize({ gtmId: GTM_CONTAINER_ID });
        }
    }, [consentGiven]);
    return {
        consentGiven,
        initialCategories,
        accept,
        decline,
        save,
    };
}
